// O fereastra de alerta centrata, cu fundal intunecat si un singur buton („Am inteles”). Pentru
// lucrurile pe care omul TREBUIE sa le vada - ex. „ora tocmai a fost rezervata” (1 oct 2026): un
// mesaj mic intr-un colt al widgetului trecea neobservat pe desktop, iar clienta nu intelegea de ce
// i-a disparut selectia.
//
// Portalata in <body>, ca selectorul „de la ora” din calendar: pe site-urile clientilor widgetul sta
// DIRECT in pagina gazda, deci fereastra trebuie sa scape de orice parinte transformat / cu overflow
// si sa stea deasupra meniurilor fixe, chat-urilor si bannerelor lor (stilurile: .bw-alert-* in
// styles.css, cu z-index maxim si !important pe ce strica des un reset CSS al gazdei).
import {t} from '../i18n';

// Fara textul in limba (server cu fisiere de limba mai vechi decat bundle-ul): nu afisam cheia bruta.
function tr(key, fallback) {
    const v = t(key);
    return v && v !== key ? v : fallback;
}

// O singura alerta deodata: a doua cerere (ex. reimprospatarea listei chiar in clipa refuzului)
// doar schimba textul celei deschise si primeste aceeasi promisiune.
let current = null;

/**
 * @param {object}  o
 * @param {string}  [o.title]     titlu scurt (gol = fara titlu)
 * @param {string}  o.message     textul
 * @param {string}  [o.button]    textul butonului (implicit „Am inteles” in limba widgetului)
 * @param {Element} [o.themeFrom] elementul widgetului, de unde copiem culorile salonului
 * @returns {Promise<void>} se rezolva cand omul inchide fereastra
 */
export function showAlert({title = '', message = '', button = '', themeFrom = null} = {}) {
    if (current) {
        current.setText(title, message);
        return current.promise;
    }

    const doc = document;
    const portal = doc.createElement('div');
    // `booking-widget` ca selectoarele si variabilele widgetului sa se aplice si aici; `bw-quick-from-portal`
    // anuleaza tot ce aduce .booking-widget la layout (div 0x0, nu umfla pagina gazda).
    portal.className = 'booking-widget bw-quick-from-portal bw-alert-portal';
    if (themeFrom) {
        try {
            const cs = getComputedStyle(themeFrom);
            ['--clr-primary', '--ff-main'].forEach((p) => {
                const v = cs.getPropertyValue(p);
                if (v && v.trim()) portal.style.setProperty(p, v.trim());
            });
        } catch (e) { /* culorile implicite */ }
    }

    const uid = 'bw-alert-' + Math.random().toString(36).slice(2, 9);
    const backdrop = doc.createElement('div');
    backdrop.className = 'bw-alert-backdrop';
    const pop = doc.createElement('div');
    pop.className = 'bw-alert-pop';
    pop.setAttribute('role', 'alertdialog');
    pop.setAttribute('aria-modal', 'true');
    const h = doc.createElement('h3');
    h.className = 'bw-alert-title';
    h.id = uid + '-t';
    const p = doc.createElement('p');
    p.className = 'bw-alert-text';
    p.id = uid + '-d';
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = 'bw-alert-btn';
    btn.textContent = button || tr('got_it', 'OK');
    pop.setAttribute('aria-labelledby', h.id);
    pop.setAttribute('aria-describedby', p.id);
    pop.appendChild(h);
    pop.appendChild(p);
    pop.appendChild(btn);
    portal.appendChild(backdrop);
    portal.appendChild(pop);
    doc.body.appendChild(portal);

    const setText = (ti, msg) => {
        h.textContent = ti || '';
        h.style.display = ti ? '' : 'none';
        p.textContent = msg || '';
    };
    setText(title, message);

    // Centrata pe ce se VEDE, nu pe pagina: pe telefon, un site gazda mai lat decat ecranul (destule
    // site-uri de saloane) face ca `position: fixed` sa se raporteze la o latime mai mare decat ecranul,
    // iar fereastra centrata „normal” cadea pe jumatate in afara. La fel dupa un zoom cu doua degete.
    // visualViewport = dreptunghiul vizibil; fara el (browsere vechi) ramane centrarea din CSS.
    const vv = window.visualViewport;
    const place = () => {
        if (!vv) return;
        pop.style.setProperty('left', (vv.offsetLeft + vv.width / 2) + 'px', 'important');
        pop.style.setProperty('top', (vv.offsetTop + vv.height / 2) + 'px', 'important');
        pop.style.setProperty('max-width', Math.max(200, vv.width - 32) + 'px', 'important');
        pop.style.setProperty('max-height', Math.max(160, vv.height - 32) + 'px', 'important');
    };
    place();
    if (vv) {
        vv.addEventListener('resize', place);
        vv.addEventListener('scroll', place);
    }

    const prevFocus = doc.activeElement;
    let resolveFn;
    const promise = new Promise((resolve) => { resolveFn = resolve; });

    const onKey = (e) => {
        if (e.key === 'Escape' || e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            close();
        } else if (e.key === 'Tab') {
            // un singur buton: focusul ramane pe el (nu fuge in pagina din spate)
            e.preventDefault();
            btn.focus();
        }
    };
    const close = () => {
        doc.removeEventListener('keydown', onKey, true);
        if (vv) {
            vv.removeEventListener('resize', place);
            vv.removeEventListener('scroll', place);
        }
        if (portal.parentNode) portal.parentNode.removeChild(portal);
        current = null;
        try {
            if (prevFocus && typeof prevFocus.focus === 'function') prevFocus.focus({preventScroll: true});
        } catch (e) { /* elementul nu mai exista */ }
        resolveFn();
    };

    btn.addEventListener('click', close);
    backdrop.addEventListener('click', close);
    doc.addEventListener('keydown', onKey, true);
    setTimeout(() => {
        try { btn.focus({preventScroll: true}); } catch (e) { btn.focus(); }
    }, 30);

    current = {setText, promise};
    return promise;
}

/** store() a refuzat o rezervare fara niciun serviciu (422 errors.serviceId). */
export function showServiceMissingAlert(message, themeFrom) {
    return showAlert({
        title: tr('pick_services', ''),
        message,
        themeFrom
    });
}

/** Alerta „ora tocmai a fost rezervata”, in limba widgetului. */
export function showSlotTakenAlert(message, themeFrom) {
    return showAlert({
        title: tr('slot_taken_title', ''),
        message: message || tr('slot_just_taken', ''),
        themeFrom
    });
}
